// 3rd-party modules
import { Tooltip, message } from "antd";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

// project modules
import Button from "../shared/button";
import Input, { Password } from "../shared/inputs/input";
import Loader from "../shared/loader";
import Select from "../shared/inputs/select";
import yup from "../../plugins/yup";
import { apiCall } from "../../helpers/apiHelper";
import { RootState } from "../../stores";
import { trimStringProps } from "../../helpers/objectHelper";

// apis
import * as AuthApi from '../../apis/authApi';
import * as CommonValueApi from '../../apis/commonValueApi';
//import * as AccountLocationApi from '../../apis/accountLocationApi';

// models
//import AccountLocationModal from "./locations/AccountLocationModal";
import ChangePasswordModel from "./ChangePasswordModel";
import ChangeEmailModal from "./ChangeEmailModal";
import ConfirmationPopup from "../shared/popup/confirmationPopup";
import ListView from "../shared/list/listView";
//import Switchable from "../shared/additionalComponents/Switchable";
import { Account } from "../../models/account";
import { AccountKiboshDevice } from "../../models/accountKiboshDevice";
//import { AccountLocation } from "../../models/accountLocation";
import { accountRegisterViewModel } from "../../models/types/auth";
import { ApiResponse } from "../../models/response";
import { CommonValue } from "../../models/commonValue";
import { CONSTANTS } from "../../helpers/defines";
import { ListViewType } from "../shared/list/useListHook";
import { SvgHamburger } from "../svg";

type Props = {
  account?: Account;
  refresh?: boolean;
  onCancel?: () => void;
  onSave?: (account: Account) => void;
};

const getAccountTypes = async (abortSignal?: AbortSignal) => {
  const response = await apiCall(CommonValueApi.getAccountTypes(abortSignal));

  return response.success ? CommonValue.toArrayOfClass(response.data?.value || []) : [];
};

export default function AccountDetails({ account, refresh, onCancel, onSave }: Props) {
  const kiboshDevice = useSelector((state: RootState) => state.user.kiboshDevice) || new AccountKiboshDevice();
  const schema = yup.object().shape({
    firstName: yup.string().label("First Name").max(50).required(),
    lastName: yup.string().label("Last Name").max(50).required(),
    accountName: yup.string().label("Account Name").max(250).required(),
    email: yup.string().label("Email").max(250).required().email(),
    phoneNumber: yup.string().label("Phone").max(20),
    accountTypeId: yup.string().label("Account Type").required(),
    password: yup.string().label("Password").min(6).max(50).test('user-id provided', 'Please enter password', (value) => (value !== undefined && value?.length > 0) || account?.accountId !== undefined),
    confirmPassword: yup.string().label("Confirm Password").max(50).oneOf([yup.ref('password'), ''], 'Passwords must match'),
  });
  const { control, handleSubmit, setValue, getValues, reset } = useForm<accountRegisterViewModel | any>({
    defaultValues: useMemo(() => account, [account]),
    resolver: yupResolver(schema),
  });
  //const [accountLocations, setAccountLocations] = useState<AccountLocation[]>([]);
  const [accountTypes, setAccountTypes] = useState<CommonValue[]>([]);
  // const [accountStatus, setAccountStatus] = useState<string>("active");
  //const [currentAccountLocation, setCurrentAccountLocation] = useState<AccountLocation>(new AccountLocation());
  // const [currentAccountStatus, setCurrentAccountStatus] = useState<string>("active");
  const [loading, setLoading] = useState(false);
  // const [loadingSave, setLoadingSave] = useState(false);
  const [isAccountNameEditable, setIsAccountNameEditable] = useState(account?.accountTypeId === 33);
  //const [isChangeBillingAdressConfirmationPopupOpen, setIsChangeBillingAdressConfirmationPopupOpen] = useState(false);
  const [isDeleteConfirmationPopupOpen, setIsDeleteConfirmationPopupOpen] = useState(false);
  // const [isAccountStatusChangeConfirmationPopupOpen, setIsAccountStatusChangeConfirmationPopupOpen] = useState(false);
  const [loadingAccountTypeList, setLoadingAccountTypeList] = useState(false);
  //const [showAccountLocationModal, setShowAccountLocationModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const abortController = new AbortController();

  // useEffect(() => {
  //   if (account) {
  //     if (account.archived) {
  //       setAccountStatus("canceled");
  //     } else {
  //       if (account.lockoutEnabled) {
  //         setAccountStatus("inactive");
  //       } else {
  //         setAccountStatus("active");
  //       }
  //     }
  //   } else {
  //     setAccountStatus("active");
  //   }
  // }, [account]);

  useEffect(() => {
    if (refresh) {
      const getAccountTypesAsync = async () => {
        setLoadingAccountTypeList(true);
        setAccountTypes(await getAccountTypes(abortController.signal));
        setLoadingAccountTypeList(false);
      }

      getAccountTypesAsync();
    }
  }, [refresh]);

  useEffect(() => {
    reset(account);

  }, [account]);

  // const onCancelClick = () => {
  //   if (onCancel) {
  //     onCancel();
  //   }
  // }

  const onAccountTypeChange = (data: any) => {
    // eslint-disable-next-line eqeqeq
    setIsAccountNameEditable(data == 33);
    setValue("accountTypeId", data);
  }

  const onChangePasswordClick = () => {
    setShowChangePasswordModal(true);
  };
  const onChangeEmailClick = () => {
    setShowChangeEmailModal(true);
  };



  const onNameChange = (e: any, fieldName: string) => {
    setValue(fieldName, e.target?.value);

    if (!isAccountNameEditable) {
      const firstName = getValues("firstName");
      const lastName = getValues("lastName");
      let accountName: string[] = [];

      if (firstName)
        accountName.push(firstName)

      if (lastName)
        accountName.push(lastName)

      setValue("accountName", accountName.join(" "))
    }
  }

  const onSubmit: SubmitHandler<accountRegisterViewModel> = async (formData: accountRegisterViewModel) => {
    let response: ApiResponse;

    setLoading(true);

    formData.userName = formData.email;

    response = await apiCall(AuthApi.updateAccount(trimStringProps(formData), abortController.signal));

    if (response.success) {
      message.success(`User ${!account?.accountId ? 'added' : 'edited'} successfully.`);

      if (account?.accountId && formData.password && formData.confirmPassword) {
        response = await apiCall(AuthApi.changePassword(trimStringProps({ username: account.username!, password: formData.password, routerReference: kiboshDevice.kiboshDeviceReference }), abortController.signal));

        if (response.success)
          message.success('Password changed successfully.');
        else
          message.error('Password changed faild.');
      }

      if (onSave) onSave(Account.toClass(response.data?.value));
    } else
      message.error(response.error?.value);

    setLoading(false);
  };

  // const onAccountStatusChange = (event: any, value: any) => {
  //   setCurrentAccountStatus(value);
  //   setIsAccountStatusChangeConfirmationPopupOpen(true);
  // }




  const renderAccountDetails = () => (
    <>
        <view
          data-border=""
          data-fit="1"
          data-direction="column"
          data-scroll=""
        >
          <group
            data-space="30"
            data-direction="column"
            // data-scroll=""
            data-length="autofit"
            style={{ maxWidth: "550px"}}
          >
            <group data-direction="column" data-gap="10">
              <Input
                control={control}
                name="firstName"
                label="First Name"
                // dataLength="150"
                size="large"
                onChange={(e) => onNameChange(e, "firstName")}
              />
            </group>
            <group data-direction="column" data-gap="10">
              <Input
                control={control}
                name="lastName"
                label="Last Name"
                // dataLength="250"
                size="large"
                onChange={(e) => onNameChange(e, "lastName")}
              />
              <separator data-horizontal=""></separator>
            </group>

            <group data-direction="column" data-gap="10">
              <Input
                type="mask"
                control={control}
                mask={CONSTANTS.DEFAULT_PHONE_NUMBER_MASK}
                maskReplacement={{ _: /\d/ }}
                name="phoneNumber"
                label="Phone"
                // dataLength="320"
                size="large"
              />
            </group>
            <group data-direction="column" data-gap="10">
              <Select
                label="Account Type"
                control={control}
                // dataLength="320"
                loading={loadingAccountTypeList}
                name="accountTypeId"
                allowSearch={true}
                onChange={onAccountTypeChange}
                options={
                  accountTypes?.map((item) => {
                    return {
                      text: item.valueCaption!,
                      value: item.commonValueId!,
                    };
                  }) || []
                }
              />
              <separator horizontal=""></separator>
            </group>
            <group data-direction="column" data-gap="10">
              <Input
                control={control}
                name="accountName"
                label={isAccountNameEditable ? "Company Name" : "Account Name"}
                // dataLength="320"
                size="large"
                disabled={!isAccountNameEditable}
              />
              <separator data-horizontal=""></separator>
            </group>
            <group data-direction="column" data-gap="10">
              <Input
                control={control}
                name="email"
                label="Email"
                // dataLength="320"
                size="large"
                disabled={ true }
              />

            </group>
            {!account?.accountId && (
              <>
                <group data-direction="column" data-gap="10">
                  <group data-gap="10">
                    <Password
                      control={control}
                      name="password"
                      label="Password"
                      // dataLength="200"
                      size="large"
                    />
                    <Password
                      control={control}
                      name="confirmPassword"
                      label="Confirm Password"
                      // dataLength="200"
                      size="large"
                    />
                  </group>
                </group>
              </>
            )}
          </group>
          <group
            data-gap="10"
            data-sticky="bottom"
            data-space="10"
            data-backdrop=""
            data-elevation="1"
            style={{ maxWidth: "550px"}}
          >
            <Button
              large
              fit
              secondary
              onClick={handleSubmit(onSubmit)}
            >
              <text>Save Changes</text>
            </Button>
            {!!account?.accountId && (
              <>
                <Button
                  large
                  data-position="right"
                  outline
                  onClick={onChangePasswordClick}
                >
                  Change Password
                </Button>
                <Button
                  large
                  data-position="right"
                  outline
                  onClick={onChangeEmailClick}
                >
                  Change Email
                </Button>
              </>
            )}
          </group>
        </view>
    </>
  )



  return (
    <view data-vertical>
      {loading && <Loader />}
      {renderAccountDetails()}
      {!!showChangePasswordModal && (
        <ChangePasswordModel
          open={showChangePasswordModal}
          closeOnSave={true}
          user={account}
          onClose={() => setShowChangePasswordModal(false)}
        />
      )}
      {!!showChangeEmailModal && (
        <ChangeEmailModal
          open={showChangeEmailModal}
          closeOnSave={true}
          user={account}
          onClose={() => setShowChangeEmailModal(false)}
        />
      )}

      {/* {isAccountStatusChangeConfirmationPopupOpen && (
        <ConfirmationPopup
          showButton={false}
          positiveButtonText="Change"
          positiveCallback={handleChangeStatusRequest}
          negativeCallback={() => {
            setIsAccountStatusChangeConfirmationPopupOpen(false);
          }}
        />
      )} */}
    </view>
  );
}
